import { DialogProps, Divider } from '@material-ui/core';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { DialogTitleWithFormStepper } from 'framework/dialogs/DialogTitleWithFormStepper';
import { OverflowDialog } from 'framework/dialogs/OverflowDialog';
import { OverflowDialogContent } from 'framework/dialogs/OverflowDialogContent';
import { FormAutocompleteFreeSoloTextField } from 'framework/forms/FormAutocompleteFreeSoloTextField';
import { FormExtendedRadioGroupField } from 'framework/forms/FormExtendedRadioGroupField';
import { FormIntegerFieldWithUnit } from 'framework/forms/FormIntegerFieldWithUnit';
import { FormMoneyField } from 'framework/forms/FormMoneyField';
import { FormSingleCheckboxField } from 'framework/forms/FormSingleCheckboxField';
import { FormTextField } from 'framework/forms/FormTextField';
import { PageableFormDialogActions } from 'framework/forms/PageableFormDialogActions';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { setFormValue } from 'framework/forms/utils/setFormValue';
import { useCacheContext } from 'framework/hooks/useCacheContext';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import {
	IInsuranceProductCatalogItemModel,
	InsuranceOrProlongedWarrantyTimeUnit,
	InsuranceOrProlongedWarrantyTimeUnits,
	IProductCatalogSearchData,
	productCatalogCommand_newInsurance,
	TimeUnit,
} from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useEffect, useState } from 'react';
import { FormSelectVatPercentageField } from 'shared/forms/FormSelectVatPercentageField';
import { useRenderTimeUnit } from 'shared/timeUnit/useRenderTimeUnit';
import * as yup from 'yup';
import { ProductCatalogFilterDataContext } from '../context/ProductCatalogFilterDataContext';

const createSchema = (strings: IStrings) => {
	return yup
		.object<IInsuranceProductCatalogItemModel>({
			name: yup.string().required(strings.formRequired(strings.name)),
			listPrice: yup.number().required('Aanbevolen verkoopsprijs'), //TODO
			vatPercentage: yup.number().required(strings.formRequired(strings.vatPercentage)).defined(),
			category: yup.string(),
			subcategory: yup.string(),
			isUnlimitedTerm: yup.boolean(),
			termUnit: yup.string().required(),
			termValue: yup.number().required(),
			canAlsoPurchase: yup.boolean(),
		})
		.defined();
};

const emptyValues: IInsuranceProductCatalogItemModel = {
	name: '',
	category: '',
	subcategory: '',
	listPrice: undefined as any,
	vatPercentage: 21,
	isUnlimitedTerm: false,
	termUnit: 'Years' as InsuranceOrProlongedWarrantyTimeUnit,
	termValue: 3,
	canAlsoPurchase: false,
};

interface IProps extends DialogProps {
	confirm: (id: string) => void;
	cancel: VoidFunction;
	previous?: VoidFunction;
}

const stepsRecord: Record<number, (keyof IInsuranceProductCatalogItemModel)[]> = {
	0: ['name', 'isUnlimitedTerm', 'termUnit', 'termValue'],
	1: ['listPrice', 'vatPercentage', 'canAlsoPurchase'],
	2: ['category', 'subcategory'],
};

export const InsuranceForm = ({ confirm, ...rest }: IProps) => {
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [data] = useCacheContext(ProductCatalogFilterDataContext);
	const [create, isSubmitting] = useFormSubmit(productCatalogCommand_newInsurance);
	const [step, setStep] = useState<number>(0);

	const handleSubmit = async (values: IInsuranceProductCatalogItemModel, helpers: FormikHelpers<IInsuranceProductCatalogItemModel>) => {
		const r = await create(values);
		if (handleFormResponse(r, helpers, stepsRecord, setStep)) {
			// navigate to detail?
			notify(strings.productCreated);
			confirm(r.result.objectId);
		}
	};

	if (data === undefined) {
		return <div></div>;
	}

	return (
		<Formik<IInsuranceProductCatalogItemModel>
			validateOnMount
			initialValues={emptyValues}
			validationSchema={createSchema(strings)}
			onSubmit={handleSubmit}>
			<Form>
				<InnerDialog
					{...rest}
					isSubmitting={isSubmitting}
					step={step}
					setStep={setStep}
					data={data}
				/>
			</Form>
		</Formik>
	);
};

interface IInnerDialogProps extends DialogProps {
	step: number;
	setStep: (val: number) => void;
	data: IProductCatalogSearchData;
	cancel: VoidFunction;
	isSubmitting: boolean;
	previous?: VoidFunction;
}

const InnerDialog = ({ step, setStep, data, cancel, isSubmitting, previous, ...rest }: IInnerDialogProps) => {
	const strings = useLocalization();
	const props = useFormikContext<IInsuranceProductCatalogItemModel>();
	const renderTimeUnit = useRenderTimeUnit();

	useEffect(() => {
		if (props.values.isUnlimitedTerm) {
			setFormValue<IInsuranceProductCatalogItemModel>('termUnit', 'Years' as InsuranceOrProlongedWarrantyTimeUnit, props);
			setFormValue<IInsuranceProductCatalogItemModel>('termValue', 3, props);
		}
		// eslint-disable-next-line
	}, [props.values.isUnlimitedTerm]);

	return (
		<OverflowDialog {...rest}>
			<DialogTitleWithFormStepper
				title={strings.newWhat(strings.insurance)}
				step={step}
				labels={[strings.name, strings.price, strings.category]}
			/>
			<OverflowDialogContent>
				{step === 0 && (
					<>
						<FormTextField<IInsuranceProductCatalogItemModel>
							name='name'
							label={strings.name}
							required
						/>
						<FormSingleCheckboxField<IInsuranceProductCatalogItemModel>
							name='isUnlimitedTerm'
							label={`${strings.withoutTerm} (${strings.unlimited})`}
						/>
						{props.values.isUnlimitedTerm === false && (
							<FormIntegerFieldWithUnit<IInsuranceProductCatalogItemModel, TimeUnit>
								name='termValue'
								unitName='termUnit'
								units={InsuranceOrProlongedWarrantyTimeUnits}
								label={strings.term}
								defaultUnit='Years'
								renderUnit={(val, unit) => renderTimeUnit(val, unit)}
							/>
						)}
					</>
				)}
				{step === 1 && (
					<>
						<FormSelectVatPercentageField<IInsuranceProductCatalogItemModel>
							name='vatPercentage'
							label={strings.vatPercentage}
							required
						/>
						<FormMoneyField<IInsuranceProductCatalogItemModel>
							name='listPrice'
							label={strings.salesPrice}
						/>
						<Divider style={{ marginTop: 16, marginBottom: 16 }} />
						<FormExtendedRadioGroupField<IInsuranceProductCatalogItemModel, boolean>
							name='canAlsoPurchase'
							label='x'
							options={[
								{ value: true, label: strings.isAlsoPurchased, caption: strings.thisWhatWillAlsoBePurchasedFromASupplier(strings.insurance) },
								{ value: false, label: strings.willNotBePurchased, caption: strings.thisWhatWillNotBePurchasedFromASupplier(strings.insurance) },
							]}
						/>
					</>
				)}
				{step === 2 && (
					<>
						<FormAutocompleteFreeSoloTextField<IInsuranceProductCatalogItemModel>
							name='category'
							label={strings.category}
							options={data.categories}
						/>
						<FormAutocompleteFreeSoloTextField<IInsuranceProductCatalogItemModel>
							name='subcategory'
							label={strings.subcategory}
							options={data.subcategories}
						/>
					</>
				)}
			</OverflowDialogContent>
			<PageableFormDialogActions
				step={step}
				setStep={setStep}
				cancel={cancel}
				isSubmitting={isSubmitting}
				submitText={strings.create}
				schema={createSchema(strings)}
				stepsRecord={stepsRecord}
				previous={previous}
			/>
		</OverflowDialog>
	);
};
